import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { FileSignature, Upload, Loader2, ClipboardPaste, UserPlus, Copy, Search, ArrowRightCircle, X } from "lucide-react";
import { trUpper } from "../lib/mw";
import { toast } from "sonner";

const MUSTERI_FIELDS = [
  ["firma", "FİRMA"], ["yetkili", "YETKİLİ"], ["gsm", "GSM"], ["s_tel", "S.TEL"],
  ["email", "EMAIL"], ["il", "İL"], ["ilce", "İLÇE"], ["adres", "ADRES"],
];

export default function SozlesmeAyristir() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selCust, setSelCust] = useState(null);
  const [searching, setSearching] = useState(false);
  const [applying, setApplying] = useState(false);

  const searchCustomers = async (q) => {
    setCustQuery(q); setSelCust(null);
    if (!q || q.trim().length < 2) { setCustResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/customers", { params: { search: q.trim(), page_size: 20 }, silent: true });
      setCustResults(data.items || []);
    } catch { setCustResults([]); }
    setSearching(false);
  };

  const applyToCustomer = async () => {
    if (!selCust) { toast.error("ÖNCE MÜŞTERİ SEÇİN"); return; }
    setApplying(true);
    try {
      const { data } = await api.post("/contracts/apply-to-customer", {
        data: { customer_id: selCust.id, rut: res.rut, teklif: res.teklif, il_cirolari: res.il_cirolari },
      });
      toast.success(`${data.firma || "MÜŞTERİ"} SATIRINA İŞLENDİ (RUT + İL + TEKLİF)`);
      setSelCust(null); setCustQuery(""); setCustResults([]);
    } catch (e) { toast.error(e.response?.data?.detail || "İŞLEME HATASI"); }
    setApplying(false);
  };

  const pick = (e) => { setFiles(Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/")).slice(0, 1)); setRes(null); };
  const onPaste = (e) => {
    for (const it of (e.clipboardData?.items || [])) {
      if (it.type && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) { setFiles([f]); setRes(null); toast.success("RESİM YAPIŞTIRILDI"); return; } }
    }
  };

  const parse = async () => {
    if (!files.length) { toast.error("ÖNCE SÖZLEŞME RESMİ SEÇİN VEYA YAPIŞTIRIN"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("files", files[0]);
    try {
      const { data } = await api.post("/contracts/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      toast.success("AYRIŞTIRILDI — KONTROL EDİN");
    } catch (e) { toast.error(e.response?.data?.detail || "AYRIŞTIRMA HATASI"); }
    setBusy(false);
  };

  const setM = (k, v) => setRes((r) => ({ ...r, musteri: { ...r.musteri, [k]: k === "email" ? v : trUpper(v) } }));

  const createCustomer = async () => {
    if (!res.musteri.firma) { toast.error("FİRMA ADI YOK — SADECE RUT/TEKLİF KULLANIN"); return; }
    setSaving(true);
    const data = { ...res.musteri, rut: res.rut, teklif: res.teklif, il_cirolari: res.il_cirolari, ozel: "SÖZLEŞME" };
    try {
      await api.post("/customers", { data });
      toast.success("YENİ MÜŞTERİ OLUŞTURULDU (RUT + TEKLİF EKLENDİ)");
      setRes(null); setFiles([]); if (inputRef.current) inputRef.current.value = "";
    } catch (e) { toast.error(e.response?.data?.detail || "KAYIT HATASI"); }
    setSaving(false);
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("KOPYALANDI"); };

  return (
    <div className="mw-fade-up space-y-4" onPaste={onPaste} data-testid="sozlesme-ayristir-page">
      <div className="flex items-center gap-2">
        <FileSignature className="h-7 w-7 text-indigo-400" />
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-white">SÖZLEŞME AYRIŞTIR</h1>
          <p className="text-sm text-slate-400">SÖZLEŞME/TEKLİF RESMİNİ YAPIŞTIR → FİRMA + RUT + TEKLİF OTOMATİK ÇIKAR. FİYAT BOŞ GELİR, SİZ DOLDURURSUNUZ.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 bg-slate-800/30 px-4 py-6 text-center hover:border-indigo-500/60">
          <Upload className="h-6 w-6 text-slate-500" />
          <span className="text-sm font-semibold text-slate-300">SÖZLEŞME RESMİ SEÇ</span>
          <span className="flex items-center gap-1 text-xs text-slate-500"><ClipboardPaste className="h-3.5 w-3.5" /> veya bu sayfaya Ctrl+V ile yapıştırın</span>
          <input ref={inputRef} data-testid="contract-file-input" type="file" accept="image/*" className="hidden" onChange={pick} />
        </label>
        {files.length > 0 && <div className="mt-2 text-xs text-slate-400">{files[0].name || "yapıştırılan görsel"} seçildi.</div>}
        <div className="mt-3 flex justify-end">
          <Button data-testid="contract-parse" onClick={parse} disabled={busy || !files.length} className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> OKUNUYOR...</> : <><FileSignature className="mr-1 h-4 w-4" /> AYRIŞTIR</>}
          </Button>
        </div>
      </div>

      {res && (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="contract-result">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-slate-400">MÜŞTERİ BİLGİLERİ (DÜZENLENEBİLİR)</div>
            <div className="grid grid-cols-2 gap-2">
              {MUSTERI_FIELDS.map(([k, l]) => (
                <div key={k} className={k === "adres" || k === "firma" ? "col-span-2" : ""}>
                  <div className="mb-0.5 text-[10px] uppercase text-slate-500">{l}</div>
                  <Input data-testid={`contract-m-${k}`} value={res.musteri[k] || ""} onChange={(e) => setM(k, e.target.value)} className="h-9 border-slate-700 bg-slate-800 text-xs text-slate-100" />
                </div>
              ))}
            </div>
            <Button data-testid="contract-create-customer" onClick={createCustomer} disabled={saving || !res.musteri.firma} className="mt-3 w-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1 h-4 w-4" />} YENİ MÜŞTERİ OLUŞTUR (RUT + TEKLİF İLE)
            </Button>
            <p className="mt-1 text-[10px] text-slate-500">Firma adı yoksa müşteri açılmaz; sağdaki RUT/TEKLİF'i kopyalayıp kullanın.</p>

            <div className="mt-4 border-t border-slate-800 pt-3" data-testid="contract-apply-section">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">VEYA MEVCUT MÜŞTERİYE İŞLE</div>
              <p className="mb-2 text-[10px] text-slate-500">Firmasız sözleşmede: sistemdeki müşteriyi seçin, RUT + İL + TEKLİF onun satırına (HESAPLA görünümüne) işlensin.</p>
              {!selCust ? (
                <>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
                    <Input data-testid="contract-cust-search" value={custQuery} onChange={(e) => searchCustomers(e.target.value)} placeholder="MÜŞTERİ ARA (FİRMA / YETKİLİ)" className="h-9 border-slate-700 bg-slate-800 pl-8 text-xs text-slate-100" />
                  </div>
                  {searching && <div className="mt-1 text-[10px] text-slate-500">ARANIYOR...</div>}
                  {custResults.length > 0 && (
                    <div className="mt-1 max-h-44 overflow-auto rounded-lg border border-slate-800 bg-slate-950">
                      {custResults.map((c) => (
                        <button key={c.id} data-testid={`contract-cust-opt-${c.id}`} onClick={() => setSelCust(c)} className="flex w-full flex-col items-start border-b border-slate-800/60 px-2 py-1.5 text-left hover:bg-slate-800">
                          <span className="text-xs font-semibold text-slate-100">{c.data?.firma || "—"}</span>
                          <span className="text-[10px] text-slate-500">{[c.data?.yetkili, c.data?.il, c.data?.ilce].filter(Boolean).join(" · ")}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {custQuery.trim().length >= 2 && !searching && custResults.length === 0 && (
                    <div className="mt-1 text-[10px] text-slate-500">MÜŞTERİ BULUNAMADI</div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-between rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-2 py-1.5">
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-indigo-200" data-testid="contract-sel-cust">{selCust.data?.firma || "—"}</span>
                    <span className="text-[10px] text-slate-400">{[selCust.data?.yetkili, selCust.data?.il].filter(Boolean).join(" · ")}</span>
                  </div>
                  <button data-testid="contract-sel-clear" onClick={() => setSelCust(null)} className="rounded p-1 text-slate-400 hover:text-red-400"><X className="h-4 w-4" /></button>
                </div>
              )}
              <Button data-testid="contract-apply-customer" onClick={applyToCustomer} disabled={applying || !selCust} className="mt-2 w-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {applying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ArrowRightCircle className="mr-1 h-4 w-4" />} MÜŞTERİYE İŞLE
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-slate-400">RUT (KISA İL KODLARI)</span>
                <button data-testid="copy-rut" onClick={() => copy(res.rut)} className="rounded p-1 text-slate-400 hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <Textarea data-testid="contract-rut" rows={2} value={res.rut} onChange={(e) => setRes((r) => ({ ...r, rut: e.target.value }))} className="border-slate-700 bg-slate-800 text-xs text-slate-100" />
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-slate-400">FİYAT TEKLİFİ (İL + DESİ + ÜRÜN, FİYAT 0)</span>
                <button data-testid="copy-teklif" onClick={() => copy(res.teklif)} className="rounded p-1 text-slate-400 hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <Textarea data-testid="contract-teklif" rows={8} value={res.teklif} onChange={(e) => setRes((r) => ({ ...r, teklif: e.target.value }))} className="border-slate-700 bg-slate-800 font-mono text-xs text-slate-100" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
